import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import ExcelJS from 'exceljs';
import * as XLSX from 'xlsx';
import { Container, Card, Button, Alert, ListGroup, Form, Row, Col, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { saveAs } from 'file-saver';
import { Helmet } from 'react-helmet-async';
import ProcessingProgress from './common/ProcessingProgress';
import useProcessingProgress from '../hooks/useProcessingProgress';
import '../styles/components.css';

const ExcelMerger = () => {
  const [files, setFiles] = useState([]);
  const [sheetNames, setSheetNames] = useState({});
  const [outputFilename, setOutputFilename] = useState('combined_workbook');
  const [error, setError] = useState('');
  
  const {
    progress,
    status,
    isComplete,
    isLoading,
    simulateProgress
  } = useProcessingProgress();

  const mergingStages = [
    { progress: 0, status: 'Starting merge process...', duration: 500 },
    { progress: 20, status: 'Loading workbooks...', duration: 800 },
    { progress: 40, status: 'Processing worksheets...', duration: 1000 },
    { progress: 70, status: 'Copying data and formatting...', duration: 1200 },
    { progress: 90, status: 'Preparing final workbook...', duration: 500 },
    { progress: 100, status: 'Complete!', duration: 500 }
  ];

  const onDrop = useCallback((acceptedFiles) => {
    // Filter only Excel files
    const excelFiles = acceptedFiles.filter(file => 
      file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      file.type === 'application/vnd.ms-excel' ||
      file.name.endsWith('.xlsx') ||
      file.name.endsWith('.xls')
    );

    setFiles(prevFiles => {
      const newFiles = [...prevFiles, ...excelFiles];
      // Initialize sheet names for new files
      const newSheetNames = { ...sheetNames };
      excelFiles.forEach(file => {
        if (!newSheetNames[file.name]) {
          newSheetNames[file.name] = '';
        }
      });
      setSheetNames(newSheetNames);
      return newFiles;
    });
  }, [sheetNames]);

  const handleRemoveFile = (index) => {
    const fileToRemove = files[index];
    setFiles(files.filter((_, i) => i !== index));
    setSheetNames(prevNames => {
      const newNames = { ...prevNames };
      delete newNames[fileToRemove.name];
      return newNames;
    });
  };

  const clearAllFiles = () => {
    setFiles([]);
    setSheetNames({});
  };

  const extractDateFromSheet = (buffer) => {
    // Use SheetJS to get correct dates
    const workbook = XLSX.read(buffer, { 
      type: 'array',
      cellDates: true,
      dateNF: 'm/d/yyyy'
    });
    
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const dateValues = {};

    // Collect all date values
    Object.keys(sheet).forEach(key => {
      if (key[0] !== '!') { // Skip metadata (keys starting with !)
        const cell = sheet[key];
        if (cell.v instanceof Date) {
          dateValues[key] = cell.v;
        }
      }
    });

    return dateValues;
  };

  const mergeExcelFiles = async () => {
    if (files.length === 0) {
      setError('Please add at least one Excel file');
      return;
    }
    if (!outputFilename.trim()) {
      setError('Please enter an output filename');
      return;
    }

    setError('');
    
    try {
      // Create a new workbook with ExcelJS
      const mergedWorkbook = new ExcelJS.Workbook();
      
      await simulateProgress(mergingStages);
      
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const buffer = await file.arrayBuffer();
        
        // Get dates using SheetJS
        const dateValues = extractDateFromSheet(buffer);
        
        // Process with ExcelJS for styling
        const sourceWorkbook = new ExcelJS.Workbook();
        await sourceWorkbook.xlsx.load(buffer);
        
        // Get the first worksheet
        const sourceWorksheet = sourceWorkbook.worksheets[0];
        
        // Create a new worksheet for each file using custom name if provided
        const customName = sheetNames[file.name];
        const worksheetName = customName || `Sheet${i + 1}_${file.name.split('.')[0].substring(0, 20)}`;
        const targetWorksheet = mergedWorkbook.addWorksheet(worksheetName);
        
        // Copy column properties
        sourceWorksheet.columns.forEach((col, index) => {
          if (col) {
            targetWorksheet.getColumn(index + 1).width = col.width || 10;
          }
        });

        // Helper function to extract cell value and style
        function extractCellData(cell) {
          const data = {
            address: cell.address,
            style: { ...cell.style }
          };

          // Check if we have a date value for this cell from SheetJS
          const cellAddress = cell.address.replace(/\d+/, ''); // Get column letter
          const rowNum = cell.address.match(/\d+/)[0]; // Get row number
          const sheetJSAddress = cellAddress + rowNum;
          
          if (dateValues[sheetJSAddress]) {
            // Use the date from SheetJS
            data.value = dateValues[sheetJSAddress];
            if (!data.style.numFmt) {
              data.style.numFmt = 'm/d/yyyy';
            }
          } else {
            // Use ExcelJS value for non-dates
            data.value = cell.value;
          }

          return data;
        }

        // Collect row and cell data
        sourceWorksheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
          const rowData = {
            number: rowNumber,
            height: row.height,
            cells: []
          };

          // First pass: collect all cell data
          const cellsInRow = [];
          row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
            const cellData = extractCellData(cell);
            cellsInRow.push({
              colNumber,
              data: cellData,
              isMerged: cell.isMerged,
              masterAddress: cell.isMerged ? cell.master.address : null
            });
          });

          // Second pass: handle duplicate values from merged cells
          let lastValue = null;
          let lastColNumber = null;
          
          cellsInRow.forEach((cell) => {
            const currentValue = JSON.stringify(cell.data.value);
            
            if (cell.isMerged) {
              if (cell.masterAddress === cell.data.address) {
                lastValue = currentValue;
                lastColNumber = cell.colNumber;
                rowData.cells.push(cell.data);
              } else {
                const clearedData = { ...cell.data, value: null };
                rowData.cells.push(clearedData);
              }
            } else {
              if (currentValue === lastValue && cell.colNumber === lastColNumber + 1) {
                const clearedData = { ...cell.data, value: null };
                rowData.cells.push(clearedData);
              } else {
                lastValue = currentValue;
                lastColNumber = cell.colNumber;
                rowData.cells.push(cell.data);
              }
            }
          });

          // Apply row and cell data
          const targetRow = targetWorksheet.getRow(rowData.number);
          if (rowData.height) {
            targetRow.height = rowData.height;
          }

          rowData.cells.forEach(cellData => {
            const targetCell = targetWorksheet.getCell(cellData.address);
            targetCell.value = cellData.value;
            if (Object.keys(cellData.style).length > 0) {
              targetCell.style = cellData.style;
            }
          });

          targetRow.commit();
        });

        // Apply worksheet properties and views
        targetWorksheet.properties = sourceWorksheet.properties;
        targetWorksheet.views = sourceWorksheet.views;
      }

      const buffer = await mergedWorkbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), `${outputFilename}.xlsx`);
      setFiles([]);
      setSheetNames({});
    } catch (err) {
      console.error('Error merging files:', err);
      setError('Error merging files: ' + err.message);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    }
  });

  return (
    <Container className="tool-container">
      <Helmet>
        <title>Excel Workbook Combiner - Merge Multiple Excel Files | Excel Toolbox</title>
        <meta name="description" content="Free tool to combine multiple Excel workbooks into one file. Merge Excel files with multiple sheets while preserving formatting. Process files securely on your device." />
        <meta name="keywords" content="combine excel files, merge excel workbooks, excel file merger, consolidate excel sheets" />
        <meta property="og:title" content="Excel Workbook Combiner - Merge Multiple Excel Files" />
        <meta property="og:description" content="Free tool to combine multiple Excel workbooks into one file. Process files securely on your local device." />
        <meta property="og:type" content="website" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://exceltoolbox.org/excel-workbook-combiner" />
      </Helmet>
      <Card className="tool-card">
        <Card.Header as="h5" className="tool-header">Excel Workbook Combiner</Card.Header>
        <Card.Body className="tool-body">
          <Row>
            <Col>
              <Card.Text className="mb-4">
                Merge multiple Excel files into a single workbook. Each file will become a separate worksheet.
              </Card.Text>

              <Form.Group className="form-section">
                <Form.Label>
                  Output Filename <span className="text-danger">*</span>
                  <OverlayTrigger
                    placement="right"
                    overlay={
                      <Tooltip>
                        The name of the combined Excel file. The .xlsx extension will be added automatically if not included.
                      </Tooltip>
                    }
                  >
                    <i className="bi bi-info-circle ms-2"></i>
                  </OverlayTrigger>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={outputFilename}
                  onChange={(e) => setOutputFilename(e.target.value)}
                  placeholder="Enter output filename"
                  required
                />
              </Form.Group>

              <div className="dropzone-container">
                <div {...getRootProps()}>
                  <input {...getInputProps()} />
                  <div className={`dropzone ${isDragActive ? 'active' : ''}`}>
                    <i className="bi bi-cloud-upload dropzone-icon"></i>
                    <p className="dropzone-text">
                      {isDragActive ? 'Drop the files here' : 'Drag and drop Excel files here, or click to select'}
                    </p>
                  </div>
                </div>
              </div>

              {files.length > 0 && (
                <div className="form-section">
                  <h6>Selected Files:</h6>
                  <ListGroup>
                    {files.map((file, index) => (
                      <ListGroup.Item key={index} className="d-flex justify-content-between align-items-center border" style={{ backgroundColor: '#ffffff' }}>
                        <div className="me-auto" style={{ flex: 1 }}>
                          <div style={{ color: '#2c3e50' }}>{file.name}</div>
                          <Form.Control
                            type="text"
                            size="sm"
                            placeholder="Enter sheet name (optional)"
                            value={sheetNames[file.name] || ''}
                            onChange={(e) => setSheetNames(prev => ({
                              ...prev,
                              [file.name]: e.target.value
                            }))}
                            className="mt-1 sheet-name-input"
                          />
                        </div>
                        <Button 
                          variant="outline-danger" 
                          size="sm"
                          onClick={() => handleRemoveFile(index)}
                        >
                          Remove
                        </Button>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                  <div className="d-grid gap-2 d-md-flex justify-content-md-end mt-2">
                    <Button 
                      variant="outline-secondary" 
                      size="sm"
                      onClick={clearAllFiles}
                    >
                      Clear All
                    </Button>
                  </div>
                </div>
              )}

              {error && <Alert variant="danger">{error}</Alert>}

              <ProcessingProgress
                progress={progress}
                status={status}
                isComplete={isComplete}
                isLoading={isLoading}
                completionMessage="Workbooks combined successfully! Your download should start automatically."
              />

              <Button
                variant="success"
                onClick={mergeExcelFiles}
                disabled={isLoading || files.length === 0 || !outputFilename.trim()}
                className="action-button"
              >
                {isLoading ? 'Combining Workbooks...' : 'Combine Workbooks'}
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default ExcelMerger;
